import { Auth0Context } from '@auth0/auth0-react';
import cookie from 'js-cookie';
import { useCallback, useContext } from 'react';

import { useAppMode } from 'component/root/AppModeProvider';

import { resolveAppMode } from './appMode';

const WHMCS_COOKIE_NAME = 'user_session';
const ROCKET_COOKIE_NAME = 'logged_in_session';

function shouldUseSessionStorage() {
  return resolveAppMode().isSdk;
}

/**
 * Gets the user's JWT from where it is stored
 */
export function getToken(): string | null | undefined {
  const useSessionStorage = shouldUseSessionStorage();
  const whmcs_cookie = useSessionStorage
    ? window.sessionStorage.getItem(WHMCS_COOKIE_NAME)
    : cookie.get(WHMCS_COOKIE_NAME);
  let rocket_cookie = useSessionStorage
    ? window.sessionStorage.getItem(ROCKET_COOKIE_NAME)
    : cookie.get(ROCKET_COOKIE_NAME);

  if (!rocket_cookie && whmcs_cookie) {
    setToken(whmcs_cookie);
    rocket_cookie = whmcs_cookie;
  }

  return rocket_cookie;
}

/**
 * Sets the user's JWT to the appropriate storage location
 * @param token
 */
export function setToken(token: string) {
  if (shouldUseSessionStorage()) {
    window.sessionStorage.setItem(ROCKET_COOKIE_NAME, token);
    return;
  }

  cookie.set(ROCKET_COOKIE_NAME, token, {
    expires: 3560,
    sameSite: 'strict',
    secure: true,
  });
}

export function removeToken() {
  if (shouldUseSessionStorage()) {
    window.sessionStorage.removeItem(WHMCS_COOKIE_NAME);
    window.sessionStorage.removeItem(ROCKET_COOKIE_NAME);
    return;
  }

  cookie.remove(ROCKET_COOKIE_NAME);
  cookie.remove(WHMCS_COOKIE_NAME);
}

/**
 * Gets user's JWT from where it is stored or from the query string if the portal is embedded in an iFrame
 */
export const getUserSession = () => {
  const { isSdk } = resolveAppMode();

  if (isSdk) {
    const qsParams = new URLSearchParams(window.location.search);
    const qsToken = qsParams.get('token');

    if (typeof qsToken !== 'undefined' && qsToken !== null) {
      setToken(qsToken);
      return qsToken;
    }
  }

  return getToken();
};

export const useLogout = () => {
  // Auth0Context accessed directly (not useAuth0()) because this hook is called
  // in WP Site Control and SDK contexts where Auth0Provider is absent.
  const auth0 = useContext(Auth0Context);
  const { usesAuth0, isSdk, isWpSiteControl, loginUrl } = useAppMode();

  const logout = useCallback(() => {
    removeToken();

    if (isSdk) {
      return;
    }

    if (isWpSiteControl) {
      window.location.href = loginUrl;
      return;
    }

    if (usesAuth0 && auth0.logout) {
      auth0.logout({
        logoutParams: {
          federated: true,
          returnTo: `${window.location.origin}${loginUrl}`,
        },
      });
    }
  }, [auth0, isSdk, isWpSiteControl, loginUrl, usesAuth0]);

  return logout;
};
